import { createSlice } from '@reduxjs/toolkit';
import {
    allocateTokens,
    fetchDealKnotConfig,
    fetchIamOrganizationSummary,
    fetchTokenSummary,
    fetchTradeKnotConfig,
    saveDealKnotConfig,
    saveTradeKnotConfig,
} from '../actions/iamApi';
import { getIamApiErrorMessage } from '../utils/iamRequest';

const defaultWhatsapp = {
    dedicatedWhatsappEnabled: false,
    whatsappCountryCode: '+91',
    whatsappNumber: '',
};

const cloneWhatsapp = (w) => ({
    dedicatedWhatsappEnabled: !!w?.dedicatedWhatsappEnabled,
    whatsappCountryCode: w?.whatsappCountryCode || '+91',
    whatsappNumber: w?.whatsappNumber ?? '',
});

const cloneLinkedUsers = (users) => (users || []).map((u) => ({ ...u }));

const initialOpenKnot = {
    openKnotsEnabled: false,
    switchBaseline: false,
    whatsapp: { ...defaultWhatsapp },
    whatsappBaseline: { ...defaultWhatsapp },
    linkedUsers: [],
    linkedUsersBaseline: [],
    tokensAvailable: null,
    tokensUtilized: null,
    allocationAmount: '',
    sectionErrors: {},
    loading: false,
    saving: false,
    baselineReady: false,
};

export const initialIamState = {
    organizationUlid: null,
    summary: null,
    orgLoading: false,
    orgError: null,
    trade: {
        enabled: false,
        baseline: false,
        loading: false,
        saving: false,
        error: null,
    },
    deal: {
        enabled: false,
        baseline: false,
        loading: false,
        saving: false,
        error: null,
    },
    openKnot: { ...initialOpenKnot },
};

const applyTokensPayload = (state, payload) => {
    const available = payload?.tokens_available;
    const utilized = payload?.tokens_utilized;
    state.openKnot.tokensAvailable =
        available != null && available !== '' ? Number(available) : null;
    state.openKnot.tokensUtilized =
        utilized != null && utilized !== '' ? Number(utilized) : null;
};

const rejectMessage = (action) =>
    action.payload?.message || getIamApiErrorMessage(action.payload || action.error, 'Request failed');

export const iamSlice = createSlice({
    name: 'iam',
    initialState: initialIamState,
    reducers: {
        resetIamState: () => ({ ...initialIamState, openKnot: { ...initialOpenKnot } }),
        setIamOrganizationUlid: (state, action) => {
            state.organizationUlid = action.payload;
        },
        setTradeKnotsEnabled: (state, action) => {
            state.trade.enabled = !!action.payload;
        },
        setDealKnotsEnabled: (state, action) => {
            state.deal.enabled = !!action.payload;
        },
        setOpenKnotsEnabled: (state, action) => {
            state.openKnot.openKnotsEnabled = !!action.payload;
        },
        setDedicatedWhatsappEnabled: (state, action) => {
            const enabled = !!action.payload;
            state.openKnot.whatsapp.dedicatedWhatsappEnabled = enabled;
            if (!enabled) {
                state.openKnot.whatsapp.whatsappNumber = '';
            }
        },
        setWhatsappNumber: (state, action) => {
            state.openKnot.whatsapp.whatsappNumber = action.payload;
        },
        setAllocationAmount: (state, action) => {
            state.openKnot.allocationAmount = action.payload;
        },
        toggleLinkedUserNotification: (state, action) => {
            const { ulid, field } = action.payload;
            state.openKnot.linkedUsers = state.openKnot.linkedUsers.map((item) =>
                item.ulid === ulid ? { ...item, [field]: !item[field] } : item
            );
        },
        setOpenKnotSectionError: (state, action) => {
            const { key, message } = action.payload;
            if (message == null || message === '') {
                delete state.openKnot.sectionErrors[key];
            } else {
                state.openKnot.sectionErrors[key] = message;
            }
        },
        clearOpenKnotSectionErrors: (state) => {
            state.openKnot.sectionErrors = {};
        },
        setOpenKnotLoading: (state, action) => {
            state.openKnot.loading = !!action.payload;
        },
        setOpenKnotSaving: (state, action) => {
            state.openKnot.saving = !!action.payload;
        },
        applyOpenKnotSnapshot: (state, action) => {
            const { openKnotsEnabled, whatsapp, linkedUsers } = action.payload;
            state.openKnot.openKnotsEnabled = !!openKnotsEnabled;
            state.openKnot.switchBaseline = !!openKnotsEnabled;
            state.openKnot.whatsapp = cloneWhatsapp(whatsapp);
            state.openKnot.whatsappBaseline = cloneWhatsapp(whatsapp);
            state.openKnot.linkedUsers = cloneLinkedUsers(linkedUsers);
            state.openKnot.linkedUsersBaseline = cloneLinkedUsers(linkedUsers);
            state.openKnot.baselineReady = true;
        },
        patchOpenKnotBaseline: (state, action) => {
            const patch = action.payload;
            if (patch.openKnotsEnabled != null) {
                state.openKnot.openKnotsEnabled = !!patch.openKnotsEnabled;
                state.openKnot.switchBaseline = !!patch.openKnotsEnabled;
            }
            if (patch.whatsapp) {
                state.openKnot.whatsapp = cloneWhatsapp(patch.whatsapp);
                state.openKnot.whatsappBaseline = cloneWhatsapp(patch.whatsapp);
            }
            if (patch.linkedUsers) {
                state.openKnot.linkedUsers = cloneLinkedUsers(patch.linkedUsers);
                state.openKnot.linkedUsersBaseline = cloneLinkedUsers(patch.linkedUsers);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIamOrganizationSummary.pending, (state) => {
                state.orgLoading = true;
                state.orgError = null;
            })
            .addCase(fetchIamOrganizationSummary.fulfilled, (state, action) => {
                state.orgLoading = false;
                state.summary = action.payload.summary;
            })
            .addCase(fetchIamOrganizationSummary.rejected, (state, action) => {
                state.orgLoading = false;
                state.orgError = rejectMessage(action);
            })
            .addCase(fetchTradeKnotConfig.pending, (state) => {
                state.trade.loading = true;
                state.trade.error = null;
            })
            .addCase(fetchTradeKnotConfig.fulfilled, (state, action) => {
                state.trade.loading = false;
                const enabled = !!(action.payload?.payload?.enabled ?? action.payload?.payload?.trade_knots_enabled);
                state.trade.enabled = enabled;
                state.trade.baseline = enabled;
            })
            .addCase(fetchTradeKnotConfig.rejected, (state, action) => {
                state.trade.loading = false;
                state.trade.error = rejectMessage(action);
            })
            .addCase(saveTradeKnotConfig.pending, (state) => {
                state.trade.saving = true;
                state.trade.error = null;
            })
            .addCase(saveTradeKnotConfig.fulfilled, (state, action) => {
                state.trade.saving = false;
                const enabled = !!action.meta.arg.tradeKnotsEnabled;
                state.trade.enabled = enabled;
                state.trade.baseline = enabled;
            })
            .addCase(saveTradeKnotConfig.rejected, (state, action) => {
                state.trade.saving = false;
                state.trade.error = rejectMessage(action);
            })
            .addCase(fetchDealKnotConfig.pending, (state) => {
                state.deal.loading = true;
                state.deal.error = null;
            })
            .addCase(fetchDealKnotConfig.fulfilled, (state, action) => {
                state.deal.loading = false;
                const enabled = !!(action.payload?.payload?.enabled ?? action.payload?.payload?.deal_knots_enabled);
                state.deal.enabled = enabled;
                state.deal.baseline = enabled;
            })
            .addCase(fetchDealKnotConfig.rejected, (state, action) => {
                state.deal.loading = false;
                state.deal.error = rejectMessage(action);
            })
            .addCase(saveDealKnotConfig.pending, (state) => {
                state.deal.saving = true;
                state.deal.error = null;
            })
            .addCase(saveDealKnotConfig.fulfilled, (state, action) => {
                state.deal.saving = false;
                const enabled = !!action.meta.arg.dealKnotsEnabled;
                state.deal.enabled = enabled;
                state.deal.baseline = enabled;
            })
            .addCase(saveDealKnotConfig.rejected, (state, action) => {
                state.deal.saving = false;
                state.deal.error = rejectMessage(action);
            })
            .addCase(fetchTokenSummary.fulfilled, (state, action) => {
                applyTokensPayload(state, action.payload?.payload);
                state.openKnot.sectionErrors.tokens = null;
            })
            .addCase(fetchTokenSummary.rejected, (state, action) => {
                state.openKnot.sectionErrors.tokens = rejectMessage(action);
            })
            .addCase(allocateTokens.fulfilled, (state) => {
                state.openKnot.sectionErrors.tokens = null;
            })
            .addCase(allocateTokens.rejected, (state, action) => {
                state.openKnot.sectionErrors.tokens = rejectMessage(action);
            });
    },
});

export const {
    resetIamState,
    setIamOrganizationUlid,
    setTradeKnotsEnabled,
    setDealKnotsEnabled,
    setOpenKnotsEnabled,
    setDedicatedWhatsappEnabled,
    setWhatsappNumber,
    setAllocationAmount,
    toggleLinkedUserNotification,
    setOpenKnotSectionError,
    clearOpenKnotSectionErrors,
    setOpenKnotLoading,
    setOpenKnotSaving,
    applyOpenKnotSnapshot,
    patchOpenKnotBaseline,
} = iamSlice.actions;

export default iamSlice.reducer;
